import io from "socket.io-client";
import api, { getSessionExpired } from "../services/api";

class SocketWorker {
  constructor(companyId, userId) {
    const sessionToken = api.defaults.headers.Authorization;

    if (!SocketWorker.instance) {
      this.companyId = companyId;
      this.userId = userId;
      this.token = sessionToken;
      this.socket = null;
      this.configureSocket();
      this.eventListeners = {};
      this.reconnectAttempts = 0;
      this.maxReconnectAttempts = 5; // Limita tentativas antes de parar
      SocketWorker.instance = this;
    }

    return SocketWorker.instance;
  }

  configureSocket() {
    // Não criar socket se sessão já expirou
    if (getSessionExpired()) {
      console.log("[Socket] Sessão expirada, não conectando.");
      return;
    }

    this.socket = io(`${process.env.REACT_APP_BACKEND_URL}/${this?.companyId}`, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 3000,
      reconnectionDelayMax: 30000,
      // Nunca desistir enquanto a sessão for válida. Com delayMax de 30s isso
      // representa no máximo ~1 tentativa a cada 30s (não sobrecarrega o servidor).
      // O encerramento legítimo é feito via getSessionExpired()/forceLogout.
      reconnectionAttempts: Infinity,
      query: { userId: this.userId, token: this.token },
    });

    this.socket.on("connect", () => {
      console.log("Conectado ao servidor Socket.IO");
      this.reconnectAttempts = 0; // Reset no sucesso
      this.reattachListeners();
    });

    this.socket.on("disconnect", (reason) => {
      console.log("Desconectado do servidor Socket.IO:", reason);

      // Desconexão manual (logout) ou sessão expirada: não reconectar.
      if (reason === "io client disconnect" || getSessionExpired()) {
        return;
      }

      // Quando o SERVIDOR força a desconexão (deploy/restart), o socket.io NÃO
      // reconecta sozinho — é preciso reabrir a conexão manualmente.
      if (reason === "io server disconnect") {
        setTimeout(() => {
          if (!getSessionExpired() && this.socket && !this.socket.connected) {
            console.log("[Socket] Reabrindo conexão após desconexão do servidor");
            this.socket.connect();
          }
        }, 3000);
      }
      // Demais motivos (transport close/error, ping timeout): a reconexão
      // automática do socket.io cuida, agora sem limite de tentativas.
    });

    this.socket.on("connect_error", (err) => {
      console.error("Erro de conexão Socket.IO:", err.message);
      // Não derrubar o socket: a reconexão automática continua tentando
      // enquanto a sessão for válida. Só paramos se a sessão expirou.
      if (getSessionExpired() && this.socket) {
        this.socket.disconnect();
      }
    });
  }

  // Re-registrar listeners existentes após reconexão
  reattachListeners() {
    const events = Object.keys(this.eventListeners);
    if (events.length > 0) {
      console.log(`[Socket] Re-registrando ${events.length} eventos após reconexão`);
      events.forEach((event) => {
        this.eventListeners[event].forEach((cb) => {
          if (this.socket && !this.socket.listeners(event).includes(cb)) {
            this.socket.on(event, cb);
          }
        });
      });
    }
  }

  on(event, callback) {
    if (getSessionExpired()) return; // Não registrar listeners se sessão expirou
    this.connect();
    if (this.socket) {
      this.socket.on(event, callback);
    }

    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  emit(event, data) {
    if (getSessionExpired()) return; // Não emitir se sessão expirou
    this.connect();
    if (this.socket) {
      this.socket.emit(event, data);
    }
  }

  off(event, callback) {
    this.connect();
    if (this.eventListeners[event]) {
      if (callback) {
        if (this.socket) this.socket.off(event, callback);
        this.eventListeners[event] = this.eventListeners[event].filter(
          (cb) => cb !== callback
        );
      } else {
        this.eventListeners[event].forEach(
          (cb) => this.socket && this.socket.off(event, cb)
        );
        delete this.eventListeners[event];
      }
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      SocketWorker.instance = null;
      this.eventListeners = {};
      this.reconnectAttempts = 0;
      console.log("Socket desconectado manualmente");
    }
  }

  reconnectAfterDelay() {
    // Não reconectar se sessão expirou
    if (getSessionExpired()) {
      console.log("[Socket] Sessão expirada, não reconectando.");
      return;
    }

    this.reconnectAttempts++;
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn("[Socket] Máximo de tentativas atingido, parando reconexão.");
      return;
    }

    // Backoff exponencial: 2s, 4s, 8s, 16s, 32s
    const delay = Math.min(2000 * Math.pow(2, this.reconnectAttempts - 1), 30000);
    console.log(`[Socket] Reconectando em ${delay / 1000}s (tentativa ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      if (getSessionExpired()) return;
      if (!this.socket || !this.socket.connected) {
        console.log("Tentando reconectar após desconexão");
        this.connect();
      }
    }, delay);
  }

  connect() {
    if (getSessionExpired()) return; // Não conectar se sessão expirou
    if (!this.socket) {
      this.configureSocket();
    } else if (!this.socket.connected) {
      // Socket existe mas está desconectado: reabrir em vez de ignorar (no-op).
      this.socket.connect();
    }
  }

  forceReconnect() {}
}

const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
